import { StoreState } from '.'
import * as actions from './actions'
import { BackendProject, Project } from '../types/project'
import {
  compare,
  recursiveMerge as mergeStates,
  toCamelCase,
} from '../object-utils'
import GeoJSON from 'ol/format/GeoJSON'
import OlGeometry from 'ol/geom/Geometry'
import OlMultiPolygon from 'ol/geom/MultiPolygon'
import ProjectResources from '../project-resources'
import Feature from 'ol/Feature'
import { areaInMeters } from '../units'
import { MultiPolygon } from '@turf/helpers'
import { PlantDetection } from '../components/plantai/types'

export default function reducer(
  state: StoreState,
  action: actions.StoreAction
): StoreState {
  switch (action.type) {
    case 'project_set': {
      const update = action.project

      const newState = {
        ...state,
        project: mergeStates(state.project, update),
      }
      return newState
    }
    case actions.PROJECT_PROPERTIES_SET: {
      const newState = {
        ...state,
        properties: mergeStates(state.properties, action.properties),
      }
      return newState
    }
    case actions.SET_ZONAL_STATISTICS:
      return { ...state, zonalStatistics: action.zonalStatistics }
    case actions.TOOL_REGISTER: {
      const newState = {
        ...state,
        tools: {
          ...state.tools,
          [action.tool.id]: action.tool,
        },
      }
      return newState
    }
    case actions.TOOL_SET_ACTIVE: {
      const { id, active } = action.tool
      const newState = {
        ...state,
        tools: {
          ...state.tools,
          [action.tool.id]: { ...state.tools[id], active },
        },
      }
      return newState
    }
    case actions.TOOL_ENABLE: {
      const { id } = action.tool
      const newState = {
        ...state,
        tools: {
          ...state.tools,
          [action.tool.id]: { ...state.tools[id], disabled: false },
        },
      }
      return newState
    }
    case actions.TOOL_DISABLE: {
      const { id } = action.tool
      const newState = {
        ...state,
        tools: {
          ...state.tools,
          [action.tool.id]: { ...state.tools[id], disabled: true },
        },
      }
      return newState
    }
    case actions.PERMISSIONS_SET: {
      const newState = {
        ...state,
        permissions: action.permissions,
      }
      return newState
    }
    case actions.MAP_SET: {
      const newState = {
        ...state,
        map: action.map,
      }
      return newState
    }
    case 'boundaries_set': {
      const writer = new GeoJSON()
      const geojson = writer.writeGeometryObject(action.geometry)
      const boundariesGeoJson = writer.writeGeometryObject(action.geometry, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857',
      }) as MultiPolygon

      const update = { cropped_extent: geojson }
      const newState = {
        ...state,
        project: mergeStates(state.project, update),
        boundariesGeoJson,
        boundariesGeometry: action.geometry,
      }

      newState.properties = {
        ...state.properties,
        area: getBoundariesArea(action.geometry),
      }

      if (state.cropSource) {
        state.cropSource.clear()
        state.cropSource.addFeature(new Feature(action.geometry))
      }

      return newState
    }
    case actions.USER_SET: {
      return {
        ...state,
        userId: action.user.id,
        permissions: action.user.permissions,
      }
    }
    // Resets state with data fetch by apiClient.getProject
    case actions.PROJECT_RESET: {
      const { project } = projectToState(action.project)

      const nextSiblings = [...state.projectSiblings]
      const siblingIndex = nextSiblings.findIndex(
        sibling => sibling.id === project.id
      )
      if (siblingIndex >= 0) {
        nextSiblings[siblingIndex] = {
          ...nextSiblings[siblingIndex],
          altitude: project.metadata?.data.relative_altitude as number,
          bands: project.bands,
          display_date: project.display_date,
          created_at: project.created_at,
          survey_date: project.survey_date,
        }
      }
      nextSiblings.sort(
        (a, b) =>
          compare(a.survey_date, b.survey_date) ||
          compare(a.created_at, b.created_at)
      )

      return {
        ...state,
        project,
        properties: {
          ...state.properties,
          ...action.project?.metadata?.data,
        },
        projectSiblings: nextSiblings,
      }
    }
    case actions.ANNOTATIONS_UPDATED: {
      return {
        ...state,
        properties: {
          ...state.properties,
          hasAnnotations: action.hasAnnotations,
        },
      }
    }
    case actions.USER_SETTINGS_UPDATED:
      return {
        ...state,
        userSettings: action.userSettings,
      }
    default:
      throw new Error(
        `Unknown action type ${(action as actions.BaseAction).type}`
      )
  }
}

export function projectToState(
  // eslint-disable-next-line camelcase
  project: BackendProject
): {
  project: Project
  boundariesGeometry: OlMultiPolygon
  boundariesGeoJson: MultiPolygon
} {
  const geojson = new GeoJSON()
  const originalGeometry = geojson.readGeometry(
    project.original_extent
  ) as OlMultiPolygon
  const originalExtent = originalGeometry.getExtent()
  const croppedExtent = project.cropped_extent || project.fields[0].geom
  const croppedGeometry = croppedExtent
    ? (geojson.readGeometry(croppedExtent) as OlMultiPolygon)
    : undefined
  const boundariesGeometry = croppedGeometry || originalGeometry
  const boundariesGeoJson = geojson.writeGeometryObject(boundariesGeometry, {
    dataProjection: 'EPSG:4326',
    featureProjection: 'EPSG:3857',
  }) as MultiPolygon

  return {
    project: {
      ...project,
      // Overwrite original cropped_extent since it might have been updated
      // with the field geometry
      cropped_extent: croppedExtent,
      originalGeometry,
      originalExtent,
      croppedGeometry,
      resources: new ProjectResources(project?.metadata?.data),
      detections: (project.plant_detections || [])
        .map(d => toCamelCase(d) as PlantDetection)
        .sort((a, b) => compare(a.createdAt, b.createdAt)),
      bands:
        project.bands ||
        (project.project_type === 'individual_images' && project.bands == null
          ? 'rgb'
          : project.bands),
    },
    boundariesGeometry,
    boundariesGeoJson,
  }
}

export function getBoundariesArea(geometry: OlGeometry): number {
  const geom = geometry.clone().transform('EPSG:3857', 'EPSG:4326')
  return areaInMeters(geom)
}
